import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore, useOnboardingStoreV2 } from '@forwardflow/store'
import {
   useGetFubStages,
   useGetPeopleTags,
} from '@forwardflow/queries/onboarding.queries'
import {
   useFubPeopleSources,
   useFubPonds,
} from '@forwardflow/queries/fub.queries'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { UserService } from '@forwardflow/services/UserService'
import {
   initializeForOrganization,
   invalidateSelection,
} from './initializeForOrganization'

// Steps that require refetching FUB data
const STEPS_REQUIRING_REFETCH: TOnboardingStep[] = [
   'stage-mapping-nurture-stages',
   'stage-mapping-appointment',
   'stage-mapping-stop-stages',
   'campaign-configuration',
   'stage-mapping-explained',
]

/**
 * Custom hook to handle all onboarding data loading and synchronization.
 * Extracts data fetching logic from the layout component to maintain separation of concerns.
 *
 * @param email - User's email address
 * @returns Object containing loading state
 */
export const useOnboardingDataLoader = (email: string | undefined) => {
   const location = useLocation()
   const navigate = useNavigate()
   const routeRef = useRef(location)
   routeRef.current = location
   const {
      initializeOnboardingData,
      setEmail,
      isLoadingOrInitialization,
      setIsLoadingOrInitialization,
      setStages,
      setTags,
      setSources,
      setPonds,
      onboardingData,
      setIsCalendarConnected,
      setIsAgent,
      adoptEmptyRecordFor,
   } = useOnboardingStoreV2()
   const { currentOrganization } = useAppStore()

   const { data: userCalendarInfo } = useGetUserCalenderInfo(!!currentOrganization)

   // Fetch FUB stages with mutate function for manual refetch
   const { data: fubStages, mutate: refetchFubStages } = useGetFubStages(
      onboardingData?.organizationId || '',
      10000,
      1,
      onboardingData?.isFUBConnected
   )

   // Fetch people tags with mutate function for manual refetch
   const { data: peopleTags, mutate: refetchPeopleTags } = useGetPeopleTags(
      onboardingData?.organizationId || currentOrganization || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Fetch FUB ponds with mutate function for manual refetch
   const { data: pondsData, mutate: refetchPonds } = useFubPonds(
      onboardingData?.organizationId || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Fetch FUB people sources with mutate function for manual refetch
   const { data: sourcesData, mutate: refetchSources } = useFubPeopleSources(
      onboardingData?.organizationId || '',
      1,
      10000,
      Boolean(onboardingData?.isFUBConnected)
   )

   // Sync people tags to store
   useEffect(() => {
      if (peopleTags) {
         setTags(peopleTags?.data?.map((item) => item?.tagName || '') || [])
      }
   }, [peopleTags, setTags])

   // Sync FUB stages to store
   useEffect(() => {
      if (fubStages) {
         setStages(
            (fubStages?.data || [])?.map((item: any) => ({
               id: item?.stageId,
               name: item?.stageName,
               description: '',
            })) || []
         )
      }
   }, [fubStages, setStages])

   // Sync ponds to store
   useEffect(() => {
      if (pondsData) {
         setPonds(
            pondsData?.map((p) => ({
               value: String(p.pondId),
               label: p.pondName,
               ...p,
            }))
         )
      }
   }, [pondsData, setPonds])

   // Sync sources to store
   useEffect(() => {
      if (sourcesData) {
         setSources(sourcesData?.map((item) => item?.leadSource || '') || [])
      }
   }, [sourcesData, setSources])

   useEffect(() => {
      if (userCalendarInfo !== undefined) {
         const isConnected = !!(userCalendarInfo && userCalendarInfo.email)
         setIsCalendarConnected(isConnected)
      }
      }, [userCalendarInfo, setIsCalendarConnected])

   // Sync email to store
   useEffect(() => {
      if (email) {
         setEmail(email)
      }
   }, [setEmail, email])

   // Initialize onboarding data when organization is available
   useEffect(() => {
      let cancelled = false
      // The sequencing lives in `initializeForOrganization` so it can be
      // tested: awaiting the load, and leaving the loading flag to the
      // initialization that owns it.
      void initializeForOrganization({
         currentOrganization,
         getUserRoleInOrganization: (organizationId: string) =>
            UserService.getUserRoleInOrganization(organizationId),
         initializeOnboardingData: async (organizationId) => {
            const result = await initializeOnboardingData(organizationId)
            const route = routeRef.current
            const state = useOnboardingStoreV2.getState()
            if (result !== 'loaded' || cancelled || state.isLoadingOrInitialization || state.onboardingSaveBlocked ||
               state.onboardingData.organizationId !== organizationId ||
               route.pathname !== '/onboarding' ||
               route.state?.organizationId !== organizationId ||
               route.state?.onboardingOrigin !== 'assistant' || !route.state?.reviewPlan) return
            state.goToStep('plan-selection')
            const remainingState = { ...route.state }
            delete remainingState.reviewPlan
            navigate('/onboarding', { replace: true, state: remainingState })
         },
         setIsLoadingOrInitialization,
         setIsAgent,
         adoptEmptyRecordFor,
      })
      // Unmounting, or selecting another organization, abandons the request
      // already out rather than letting it land on what is on screen after.
      return () => {
         cancelled = true
         invalidateSelection()
      }
   }, [
      currentOrganization,
      initializeOnboardingData,
      setIsLoadingOrInitialization,
      setIsAgent,
      adoptEmptyRecordFor,
      navigate,
   ])

   // Track previous step to detect changes
   const prevStepRef = useRef(onboardingData?.currentStep)

   // Refetch data when onboarding step changes to specific steps
   useEffect(() => {
      const currentStep = onboardingData?.currentStep

      // Only refetch if step actually changed (not initial render) and is one of the required steps
      if (
         prevStepRef.current &&
         prevStepRef.current !== currentStep &&
         currentStep &&
         STEPS_REQUIRING_REFETCH.includes(currentStep)
      ) {
         // Refetch all FUB-related data when step changes to required steps
         if (onboardingData?.isFUBConnected) {
            refetchFubStages()
            refetchPeopleTags()
            refetchPonds()
            refetchSources()
         }
      }

      // Update the previous step reference
      prevStepRef.current = currentStep
   }, [
      onboardingData?.currentStep,
      onboardingData?.isFUBConnected,
      refetchFubStages,
      refetchPeopleTags,
      refetchPonds,
      refetchSources,
   ])

   // useEffect(() => {
   //    transformStageMappingDataAndUpdate()
   // }, [onboardingData.tempStageGroups, transformStageMappingDataAndUpdate])

   return {
      isLoading: isLoadingOrInitialization,
      currentOrganization,
   }
}
